import { useEffect, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';

import type { SwiperRef } from 'swiper/react';

import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';
import DynamicIcon from '@/components/icons/DynamicIcon';

import { useVisibleSlides } from '@/components/blocks/ProductCarousel/utils/useVisibleSlides';
import { rootSx } from '@/components/blocks/ProductCarousel/styles/productCarouselNavigation';

export const ProductCarouselNavigation = ({
	swiperRef,
	totalSlides,
}: {
	swiperRef: React.RefObject<SwiperRef>;
	totalSlides: number;
}) => {
	const { step } = useVisibleSlides();

	const [currentSlide, setCurrentSlide] = useState(0);
	const [isEnd, setIsEnd] = useState(false);

	const totalPages = Math.ceil(totalSlides / step);
	const currentPage = isEnd ? totalPages - 1 : Math.floor(currentSlide / step);

	const isPrevDisabled = currentPage === 0;
	const isNextDisabled = currentPage === totalPages - 1;

	const goToPage = (page: number) => {
		const swiper = swiperRef.current?.swiper;
		if (!swiper) return;

		const newSlide = Math.max(Math.min(page * step, totalSlides - 1), 0);
		swiper.slideTo(newSlide);
	};
	const onPrev = () => goToPage(currentPage - 1);
	const onNext = () => goToPage(currentPage + 1);

	useEffect(() => {
		const swiper = swiperRef.current?.swiper;
		if (!swiper) return;

		const onUpdate = () => {
			setCurrentSlide(swiper.activeIndex);
			setIsEnd(swiper.isEnd);
		};

		swiper.on('slideChange', onUpdate);
		swiper.on('resize', onUpdate);

		return () => {
			swiper.off('slideChange', onUpdate);
			swiper.off('resize', onUpdate);
		};
	}, [swiperRef]);

	if (totalPages <= 1) return null;

	return (
		<Stack direction="row" spacing={0} sx={rootSx}>
			{/* eslint-disable-next-line react/forbid-component-props */}
			<IconButton disabled={isPrevDisabled} onClick={onPrev} className='carousel__back-button'>
				<DynamicIcon name={MATERIAL_SYMBOL_ICONS.chevronLeft} />
			</IconButton>

			<Typography variant="bodyBaseline">{`${currentPage + 1}/${totalPages}`}</Typography>

			{/* eslint-disable-next-line react/forbid-component-props */}
			<IconButton disabled={isNextDisabled} onClick={onNext} className='carousel__next-button'>
				<DynamicIcon name={MATERIAL_SYMBOL_ICONS.chevronRight} />
			</IconButton>
		</Stack>
	);
};
